/*
 * Page for setting up and maintaining integration settings for FreshBooks.
 *
 * The account delegated auths map table contains the mappings between an entity in AppBind and an entity in FreshBooks.
 * When the user selects an entity from a list in FreshBooks (e.g. select an expense category), we have the shape of
 * the category in FreshBooks:
 * 
 *   {
 *      "category": "Accident Insurance",
 *      "categoryid": 4243789,
 *      "created_at": "2022-02-03 16:19:18",
 *      "id": 4243789,
 *      "is_cogs": false,
 *      "is_editable": false,
 *      "parentid": 4243786,
 *      "updated_at": "2022-02-03 16:19:18",
 *      "vis_state": 0
 *   }
 * 
 * However, the shape we're sending to the AppBind api is:
 * 
 *  {
 *     accountDelegatedAuthsMapId: uuid,
 *     mapName:                    map name (e.g. "AppBind Fee Category")
 *     entityId:                   object id in FreshBooks,
 *     entityName:                 object name in FreshBooks (e.g. "ExpenseCategory")
 *     entityDescription:          specific object descriptive text (e.g. "Accident Insurance")
 *  }
 * 
 * The list of valid values will have the FreshBooks category shape, so upon a match, "id" will be mapped to entityId,
 * and "category" will be mapped to entityDescription.
 */
import React, { Component } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { QueryRenderer } from 'react-relay';
import { Redirect } from 'react-router-dom';
import AbEnvironment from '../auth/AbEnvironment';
import FreshBooksSetupFetch from './FreshBooksSetupFetch';
import IntegrationSetupMutation from './IntegrationSetupMutation';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import { Backdrop, Button, Grid, Paper } from '@mui/material';
import { FormControl, Input, NativeSelect, Typography  } from '@mui/material';

import PageTitle from '../ui/PageTitle';
import LoadingSpinner from '../ui/LoadingSpinner';
import ServerError from '../ui/ServerError';
import AppBindStyles from '../../AppBindStyles';
import AppLogo from '../ui/AppLogo';
import { format } from 'date-fns';

const abEnvironment = new AbEnvironment();
const IntegrationSetupFreshbooksQuery = graphql`
query IntegrationSetupFreshbooksQuery($accountDelegatedAuthsId: UUID!) {
  accountDelegatedAuthsTByAccountDelegatedAuthsId(accountDelegatedAuthsId: $accountDelegatedAuthsId) {
    appName
    authType
    appData
    environment
    effectiveDate
    appIntegrationTByAppIntegrationId {
      appDescription
      appLogo
    }
    accountDelegatedAuthsMapTsByAccountDelegatedAuthsId {
      totalCount
      nodes {
        accountDelegatedAuthsMapId
        mapName
        entityId
        entityName
        entityDescription
      }
    }
    accountTByAccountId {
      accountName
      paymentSourceTsByAccountId(condition: { psStatus: 0 }) {
        totalCount
        nodes {
          psId
          psStatus
          psType
          ccNum
          ccExpy
          ccBrand
          ccLast4
          extSource
          fiName
          achAccountNum
        }
      }
    }
  }
}
`;

const styleSheet = theme => ({
    root: {
      flexGrow: 1,
      //marginTop: 10,
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: '#fff'
    },
    backdropDiv: {
      width: '90%'
    },
    flex: {
      flex: 1,
    },
    vendorSelect: {
      width: 420,
    },
    vendorNameField: {
      width: 360,
    },
    accountSelect: {
      width: 370,
    },
    codeField: {
      width: 100,
    },
    descField: {
      minWidth: 486,
    },
    paper: {
        [theme.breakpoints.up('xs')]: {
          padding: theme.spacing(1),
        },
        [theme.breakpoints.up('sm')]: {
          padding: theme.spacing(2),
        },
        [theme.breakpoints.up('md')]: {
          padding: theme.spacing(3),
        },
        textAlign: 'left',
        color: theme.palette.text.secondary,
        maxWidth: 1200,
        marginTop: 16,
    },
    sectionPaper: {
      padding: theme.spacing(2),
      color: theme.palette.text.secondary
    },
    howBlock: {
      maxWidth: 600
    },
    appTitle: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    description: {
        //color: '#666666'
    }
});

class IntegrationSetupFreshbooks extends Component {

  constructor(props) {
    super(props);

    this.state = {
      mountComplete:         false,
      environment:           null,
      businesses:            [],   // FreshBooks businesses that the FreshBooks user credentials can access
      expense_category_list: [],   // All expense categories
      expense_category_exp:  [],   // Non-cogs expense categories
      saasfee:               null, // SaaS app cost passed to customer.
      abfee:                 null, // AppBind fees - will default map to "bookkeeping fee".  AppBind and payment fees passed to customer.
      effectiveDate:         null,
      lookup_error:          null
    };

    this.fbOnboarding = false;

    this.defaults = {
      saas: 'Online Services',
      cogs: 'Cost of Goods Sold',
      fees: 'Bank Fees'
    };
  }

  lookupFreshBooksSettings = () => {
    // First lookup the environment information from the db.
    const { isAuthenticatedReseller, needAssent, callPartnerApi, refreshUser, logError } = this.props.auth;
    if (!isAuthenticatedReseller() || needAssent()) {
      return;
    }

    const params = new URLSearchParams(this.props.location.search);
    let adaId = params.get('adaId');

    let environment = '';
    let appData = null;
    let businesses = [];
    let ec_list = [];      // All expense categories
    let ec_exp_list = [];  // Normal expenses
    let theExpenseResponse;

    FreshBooksSetupFetch.fetch(adaId).toPromise()
    .then(fbsf => {
      if (fbsf.accountDelegatedAuthsTByAccountDelegatedAuthsId == null) {
        return null;
      }

      // Environment and app data
      environment = fbsf.accountDelegatedAuthsTByAccountDelegatedAuthsId.environment;
      appData     = fbsf.accountDelegatedAuthsTByAccountDelegatedAuthsId.appData;

      // Put businesses in an array
      if (appData.business != null) {
        // This is a deprecated form of FreshBooks appData (we used to store just one business).
        businesses.push(appData.business);
      } else if (appData.business_memberships != null) {
        for (let i=0; i<appData.business_memberships.length; i++) {
          businesses.push(appData.business_memberships[i]);
        }
      }

      // If environment is set, lookup expense categories
      if (environment != null && environment !== '') {
        let payload = {
          operation: "FreshBooksGetExpenseCategories"
        };
        return callPartnerApi('appbind/generic', 'POST', payload);
      } else {
        return 'noenv';
      }
    })
    .then(resp => {
      if (resp == null || resp === 'noenv') {
        return resp;
      }
      return resp.json();
    })
    .then(response => {
      if (response == null || response === 'noenv') {
        return response;
      }

      theExpenseResponse = response;

      // Intercept here to refresh integration information cache if needed.
      let integratedApps = abEnvironment.getUserAttribute('a_integrations');
      if (integratedApps != null && (integratedApps === 'FreshBooks' || integratedApps.includes('FreshBooks'))) {
        // Integration is already in cache, nothing to do
        return theExpenseResponse;
      } else {
        return refreshUser(null, 'integrations');
      }
    })
    .then(response => {
      if (response == null || response === 'noenv') {
        this.setState({mountComplete: true,
                       businesses:    businesses,
                       environment:   environment});
      } else {
        if (theExpenseResponse != null && theExpenseResponse.body != null && theExpenseResponse.body.body != null && theExpenseResponse.body.body.response != null) {
          ec_list = theExpenseResponse.body.body.response.result.categories;
          ec_exp_list = ec_list;
        } else {
          // Throw
          throw(response);
        }

        this.setState({mountComplete:         true,
                       businesses:            businesses,
                       environment:           environment,
                       expense_category_list: ec_list,
                       expense_category_exp:  ec_exp_list});
      }
    })
    .catch(err => {
      logError('Exception in IntegrationSetupFreshbooks.js: ' + err);
      this.setState({lookup_error: JSON.stringify(err), mountComplete: true});
    })
  }

  componentDidMount() {
    // Handle onboarding case
    this.fbOnboarding = false;
    if (localStorage.getItem("int_onboarding") === "fb") {
      this.fbOnboarding = true;
      // localStorage.removeItem("int_onboarding");
    }

    // Load hook
    this.lookupFreshBooksSettings();
  }

  disconnectFromApp(adaId, onboarding) {
    const { callPartnerApi, refreshUser } = this.props.auth;

    if (! adaId) {
      return;
    }

    let payload = {
      operation: "OAuth2Revoke",
      params: {
        accountDelegatedAuthsId:  adaId
      }
    };

    let theLink = null;
    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      theLink = data.link;
      return refreshUser(null, 'integrations');
    })
    .then(resp => {
      // If this is a disconnect in onboarding, go back to onboarding
      if (onboarding) {
        localStorage.removeItem("int_onboarding");
        this.props.history.push('/home');
      } else if (theLink != null) {
        window.location.replace(theLink);
      } else {
        window.location.reload();
      }
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  isEnvironmentSaveDisabled() {
    if (this.state.environment == null || this.state.environment === '') {
      return true;
    }
    return false;
  }

  /*
   * Should save be disabled?  We only want it enabled if there are changes to save, and
   * all required values are set.  The parameters coming in will be blank if a new vendor
   * or account is to be created, and non-blank if an existing id is being used.
   * So if blank, require that additional fields are set.
   * 
   * There is one exception to this; if this is the first time in, and all values are the defaults,
   * allow a save.
   * 
   */
  isSaveDisabled(saasfeeMap, abfeeMap) {
    // If there are no maps, disable
    if (saasfeeMap == null && abfeeMap == null) {
      return true;
    }

    // Edge case - if any map is set to its descriptor label (so entity id is blank), show as disabled. However, allow the
    // blank for the SaaS cogs map
    if ( (saasfeeMap  != null && saasfeeMap.entityId === '') ||
         (abfeeMap    != null && abfeeMap.entityId === '') ) {
      return true;
    }


    // If anything has changed, save is enabled
    if ( (saasfeeMap  != null && saasfeeMap.theState  === 'changed') ||
         (abfeeMap    != null && abfeeMap.theState    === 'changed') ) {
      return false;
    }

    // If all defaults are set, save is enabled
    if (saasfeeMap != null && saasfeeMap.theState === 'default' &&
        abfeeMap != null && abfeeMap.theState === 'default') {
      return false;
    }

    // Disabled
    return true;
  }

  copyObjectInfo(sourceObj, targetObj) {
    targetObj.accountDelegatedAuthsMapId = sourceObj.accountDelegatedAuthsMapId;
    targetObj.mapName                    = sourceObj.mapName;
    targetObj.entityId                   = sourceObj.entityId;
    targetObj.entityName                 = sourceObj.entityName;
    targetObj.entityDescription          = sourceObj.entityDescription;
  }

  hydrateObject(mapName, entityName, propsObj, stateObj, stateList, defaultMatch) {
    let mapObj = {
      accountDelegatedAuthsMapId: null,
      mapName: mapName,
      entityId: '',
      entityName: entityName,
      entityDescription: '',
      theState: ''
    };

    // If there are state values, populate from state
    if (stateObj != null) {
      this.copyObjectInfo(stateObj, mapObj);
      mapObj.theState = 'changed';

      // Was this changed back to the original value?
      if (propsObj != null &&
          stateObj.mapName           === propsObj.mapName &&
          stateObj.entityId          === propsObj.entityId &&
          stateObj.entityName        === propsObj.entityName &&
          stateObj.entityDescription === propsObj.entityDescription) {
        mapObj.theState = 'saved';
      }
    } else if (propsObj != null) {
      this.copyObjectInfo(propsObj, mapObj);
      mapObj.theState = 'saved';
    } else if (defaultMatch != null) {
      // Go with a default if available.  
      for (let j=0; j<stateList.length; j++) {
        if (stateList[j].category === defaultMatch) {
          mapObj.entityId = stateList[j].id;
          mapObj.entityDescription = defaultMatch;
          mapObj.theState = 'default';
          break;
        }
      }
    }
    
    if (mapObj.entityId != null && mapObj.entityId !== '') {
      let idMatch = false;
      for (let j=0; j<stateList.length; j++) {
        if (Number(stateList[j].id) === Number(mapObj.entityId)) {
          idMatch = true;
          break;
        }
      }
      if (idMatch === false) {
        mapObj.entityId = '';
      }
    }
    return mapObj;
  }

  /**
   * Return text as a 'YYYY-MM-DD' string.
   * 
   * This will either be a date on the state, or a date string from props.
   */
  getEffectiveDateString(propsEffectiveDate) {
    let theDate = this.getEffectiveDate(propsEffectiveDate);
    let theEffectiveDateText = format(theDate, 'yyyy-MM-dd');
    return theEffectiveDateText;
  }


  /***************************************
   * Generic set/get convenience methods
   **************************************/
  selectMappedEntry(stateAttrName, validValueList, anId, currentObj) {
    let mapObj = {
      accountDelegatedAuthsMapId: currentObj.accountDelegatedAuthsMapId,
      mapName: currentObj.mapName,
      entityId: currentObj.entityId,
      entityName: currentObj.entityName,
      entityDescription: currentObj.entityDescription
    };

    if (anId != null && anId !=='') {
      mapObj.entityId = anId;

      // Lookup the entry (expense category) from the list matching on id
      for (let i=0; i<validValueList.length; i++) {
        let theEc = validValueList[i];
        if (Number(theEc.id) === Number(anId)) {
          mapObj.entityDescription = theEc.category;
          break;
        }
      }
    } else {
      mapObj.entityId = '';
      mapObj.entityDescription = '';
    }
    this.setState({[stateAttrName]: mapObj});
  }

  selectBusiness(theEnvironment) {
    this.setState({environment: theEnvironment});
  }

  saveEnvironment(adaId) {
    // Save the environment, and call lookupFreshBooksSetting
    if (this.state.environment != null && this.state.environment) {
      IntegrationSetupMutation.commit(adaId, this.state.environment)
      .then(response => {
        this.lookupFreshBooksSettings();
      })
      .catch(error => {
        // Swallow
        console.log("Error: " + error);
      });
    }
  }

  /* Call API to save settings, as this may involve api calls to push entities to the integrated app */
  saveSettings(adaId, saasfeeMap, abfeeMap, onboarding) {
    const { callPartnerApi } = this.props.auth;

    // Shouldn't ever happen.
    if (this.isSaveDisabled(saasfeeMap, abfeeMap)) {
      return;
    }

    // effective date isn't currently used for FreshBooks
    let propsEffectiveDate = new Date();

    // If parms are non-null, we have mappings.
    let payload = {
      operation: "FreshBooksUpdateMappings",
      params: {
        accountDelegatedAuthsId:  adaId,
        effectiveDate: this.getEffectiveDateString(propsEffectiveDate),
        maps: []
      }
    };

    // The payload should only contain changes.  This means that the state values are what should be sent.  However, we also want
    // to enable default setting as a convenience.  So instead of directly using state values, we depend on the hydration method
    // to populate the maps appropriately, and then will add to the payload if state has changed, or if the default value is
    // set.
    let maps = [];
    if (saasfeeMap  != null && (saasfeeMap.theState  === 'changed' || saasfeeMap.theState  === 'default')) { maps.push(saasfeeMap);  }
    if (abfeeMap    != null && (abfeeMap.theState    === 'changed' || abfeeMap.theState    === 'default')) { maps.push(abfeeMap);    }

    if (maps.length > 0) {
      payload.params.maps = maps;
    }

    Promise.resolve()
    .then(() => { 
        return callPartnerApi('appbind/generic',
                              'POST',
                              payload);
    })
    .then(response => {
        return response.json();
    })
    .then(data => {
      if (data.link) {
        window.location.replace(data.link);
      } else {
        // If this is a save in onboarding, go back at this point
        if (onboarding) {
          localStorage.removeItem("int_onboarding");
          this.props.history.push('/home');
        } else {
          window.location.reload();
        }
      }
    })
    .catch(err => {
      // console.log('err');
      window.location.reload();
    });
  }

  render() {
    const { isAuthenticatedReseller, needAssent, logError } = this.props.auth;
    const classes = this.props.classes;

    if (!isAuthenticatedReseller() || needAssent()) { 
      return ( <Redirect to='/home' /> );
    }

    const params = new URLSearchParams(this.props.location.search);
    let adaId = params.get('adaId');

    let backText = "Settings";

    // Don't show the page until load is complete
    if (!this.state.mountComplete === true) {
      return (
        <LoadingSpinner />
      );
    }

    return (
      <QueryRenderer
        environment={abEnvironment.getEnvironment()}
        query={IntegrationSetupFreshbooksQuery}
        variables={{accountDelegatedAuthsId: adaId}}
        render={({error, props})=> {
          if (error) {
            logError(error.message);
            return (
              <ServerError/>
            );
          } else if (props) {
            let backLink = 'settings';
            if (props.accountDelegatedAuthsTByAccountDelegatedAuthsId === null)
            {              
              return (
                <PageTitle title="Integration not found" showBackButton={true} name={backText} backLink={backLink} history={this.props.history}/>
              );
            }

            // Spinner should be done by now - load is completed - but if no error, show a spinner
            // if (this.state.businesses.length === 0 || this.state.expense_category_list == null || this.state.expense_category_list.length === 0) {
            if (this.state.businesses.length === 0) {
              if (this.state.lookup_error != null) {
                return (
                  <div className={classes.root}>
                    <PageTitle title="Unable to load integration" showBackButton={true} name={backText} backLink={backLink} history={this.props.history}/>
                    <div>
                      <Button color="primary" variant="outlined" onClick={()=> {this.disconnectFromApp(adaId, this.fbOnboarding)}}>Disconnect from FreshBooks</Button>
                    </div>
                  </div>
                );
              }
              return (
                <LoadingSpinner />
              );
            }

            let theIntegration = props.accountDelegatedAuthsTByAccountDelegatedAuthsId;
            let theLogo = theIntegration.appIntegrationTByAppIntegrationId.appLogo;
            let theDescription = theIntegration.appIntegrationTByAppIntegrationId.appDescription;

            let businesses      = this.state.businesses;
            let theEnvironment  = this.state.environment;

            if (theIntegration.appData && theIntegration.appName === 'FreshBooks') {
              // let companyName = theIntegration.appData.CompanyInfo[0].CompanyName;
              // theDescription = theDescription + ' ' + companyName;
            } else {
              return (
                <div className={classes.root}>
                  <PageTitle title="Unable to load integration" showBackButton={true} name={backText} backLink={backLink} history={this.props.history}/>
                  <div>
                    <Button color="primary" variant="outlined" onClick={()=> {this.disconnectFromApp(adaId, this.fbOnboarding)}}>Disconnect from FreshBooks</Button>
                  </div>
                </div>
              );
            }

            // If expense_category_list isn't set, we're still setting environment, so show a dropdown to select a business.  Otherwise, show
            // the expense category dropdowns.
            let integrationPage;
            if (this.state.expense_category_list.length === 0) {
              integrationPage = (
                <React.Fragment>
                  {this.fbOnboarding === false &&
                  <PageTitle title="Manage integration" showBackButton={true} name={backText} backLink={backLink} history={this.props.history}/>}

                  <Paper className={classes.paper}>
                    <Grid container spacing={1} direction="row" justifyContent="flex-start">
                      <Grid item>
                        <AppLogo logoPath={theLogo} appName={theIntegration.appName} imageHeight='48px'/>
                      </Grid>
                      <Grid item>
                        <Typography variant="h6" className={classes.appTitle}>{theIntegration.appName}</Typography>
                        <Typography variant="body2" className={classes.description}>{theDescription}</Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Paper className={classes.sectionPaper}>
                          <Typography variant="body2" style={{float: 'right', margin:"0 0 16px 32px"}} align="right" color="secondary">
                            <Button color="primary" variant="outlined" size="small" 
                              onClick={() => { window.open('https://www.appbind.com/connect-freshbooks-to-appbind/'); }}>
                              Learn more
                            </Button>
                          </Typography>
                          <div className={classes.container}>
                            <Typography variant="h6">
                              How does the FreshBooks integration work?
                            </Typography>
                            <Typography variant="body2">
                              AppBind will automatically record your expenses, create invoices for your clients, and book your revenue in FreshBooks.
                            </Typography>
                          </div>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                          Select a FreshBooks business to connect
                        </Typography>
                        <Typography variant="body2">
                          You're associated with {businesses.length} businesses in FreshBooks.  Please select the business you want to connect to AppBind.
                        </Typography>
                      </Grid>
                      <Grid item>
                        <FormControl className={classes.accountSelect}>
                          <NativeSelect
                            value={theEnvironment}
                            input={<Input name="business" id="business" />}
                            onChange={(e) => this.selectBusiness(e.target.value) }
                          >
                            {businesses.map(n => {
                              let b = n.business;
                              let theId = b.account_id + ',' + b.id;
                              return(<option key={theId} value={theId}>{b.name}</option>);
                            })}
                            <option key="" value="">Select a business</option>
                          </NativeSelect>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}/>

                      <Grid item>
                        <Button color="primary" variant="contained" disabled={this.isEnvironmentSaveDisabled()} 
                                onClick={()=> {
                                  this.saveEnvironment(adaId)
                                }}>
                          Connect
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button color="primary" variant="outlined" onClick={()=> {this.disconnectFromApp(adaId, this.fbOnboarding)}}>Disconnect from FreshBooks</Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </React.Fragment>
              );

            } else {
              // Get the business name from the list of businesses where the id matches the id in the environment
              // environment = appData.business_memberships[0].business.account_id + ',' + appData.business_memberships[0].business.id;
              // Parse business id
              let theIds = theEnvironment.split(',');
              let business_id = theIds[1];
              let theBusinessName = 'Business';
              // look up
              for (let i=0; i<businesses.length; i++) {
                let businessId = '' + businesses[i].business.id;
                if (businessId === business_id) {
                  theBusinessName = businesses[i].business.name;
                  break;
                }
              }

              let theMaps = theIntegration.accountDelegatedAuthsMapTsByAccountDelegatedAuthsId;

              let saasFeeExpenseCategory    = null;
              let appBindFeeExpenseCategory = null;

              let mapsCount = theMaps.totalCount;

              if (mapsCount > 0) {
                theMaps.nodes.forEach(n => {
                  if (n.mapName === "SaaSFeeAccount") {
                    saasFeeExpenseCategory = n;
                  } else if (n.mapName === "AppBindFeeAccount") {
                    appBindFeeExpenseCategory = n;
                  }
                });
              }

              let theSaaSFeeExpenseCategory  = this.hydrateObject('SaaSFeeAccount',    'ExpenseCategory', saasFeeExpenseCategory,    this.state.saasfee,  this.state.expense_category_list, this.defaults.saas);
              let theAppBindExpenseCategory  = this.hydrateObject('AppBindFeeAccount', 'ExpenseCategory', appBindFeeExpenseCategory, this.state.abfee,    this.state.expense_category_list, this.defaults.fees);

              integrationPage = (
                <React.Fragment>
                  {this.fbOnboarding === false &&
                  <PageTitle title="Manage integration" showBackButton={true} name={backText} backLink={backLink} history={this.props.history}/>}

                  <Paper className={classes.paper}>
                    <Grid container spacing={1} direction="row" justifyContent="flex-start">
                      <Grid item>
                        <AppLogo logoPath={theLogo} appName={theIntegration.appName} imageHeight='48px'/>
                      </Grid>
                      <Grid item>
                        <Typography variant="h6" className={classes.appTitle}>{theIntegration.appName}</Typography>
                        <Typography variant="body2" className={classes.description}>{theDescription}</Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Paper className={classes.sectionPaper}>
                          <Typography variant="body2" style={{float: 'right', margin:"0 0 16px 32px"}} align="right" color="secondary">
                            <Button color="primary" variant="outlined" size="small" 
                              onClick={() => { window.open('https://www.appbind.com/connect-freshbooks-to-appbind/'); }}>
                              Learn more
                            </Button>
                          </Typography>
                          <div className={classes.container}>
                            <Typography variant="h6">
                              How does the FreshBooks integration work?
                            </Typography>
                            <Typography variant="body2">
                              AppBind will automatically record your expenses, create invoices for your clients, and book your revenue in FreshBooks.
                            </Typography>
                          </div>
                        </Paper>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                          Connected to {theBusinessName}
                        </Typography>
                        <Typography variant="body2">
                          Your AppBind account is connected to FreshBooks business {theBusinessName}.
                        </Typography>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                          Expense Category for subscriptions
                        </Typography>
                        <Typography variant="body2" className={classes.description}>What FreshBooks Expense Category should subscription expenses be mapped to?</Typography>
                      </Grid>
                      <Grid item>
                        <FormControl className={classes.accountSelect}>
                          <NativeSelect
                            value={theSaaSFeeExpenseCategory.entityId}
                            input={<Input name="saasfee" id="saasfee" />}
                            onChange={(e) => this.selectMappedEntry('saasfee', this.state.expense_category_list, e.target.value, theSaaSFeeExpenseCategory) }
                          >
                            {this.state.expense_category_list.map(n => {
                              return(<option key={n.id} value={n.id}>{n.category}</option>);
                            })}
                            <option key="" value="">Select an Expense Category</option>
                          </NativeSelect>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="h6" style={AppBindStyles.sectionTitle}>
                          Expense Category for transaction fees
                        </Typography>
                        <Typography variant="body2" className={classes.description}>What FreshBooks Expense Category should payment processing and AppBind bookkeeping fees be mapped to?</Typography>
                      </Grid>
                      <Grid item>
                        <FormControl className={classes.accountSelect}>
                          <NativeSelect
                            value={theAppBindExpenseCategory.entityId}
                            input={<Input name="abfee" id="abfee" />}
                            onChange={(e) => this.selectMappedEntry('abfee', this.state.expense_category_list, e.target.value, theAppBindExpenseCategory) }
                          >
                            {this.state.expense_category_list.map(n => {
                              return(<option key={n.id} value={n.id}>{n.category}</option>);
                          })}
                            <option key="" value="">Select an Expense Category</option>
                          </NativeSelect>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}/>

                      <Grid item>
                        <Button color="primary" variant="contained" disabled={this.isSaveDisabled(theSaaSFeeExpenseCategory, theAppBindExpenseCategory)} 
                                onClick={()=> {
                                  this.saveSettings(adaId, theSaaSFeeExpenseCategory, theAppBindExpenseCategory, this.fbOnboarding)
                                }}>
                          Save settings
                        </Button>
                      </Grid>
                      <Grid item>
                        <Button color="primary" variant="outlined" onClick={()=> {this.disconnectFromApp(adaId, this.fbOnboarding)}}>Disconnect from FreshBooks</Button>
                      </Grid>
                    </Grid>
                  </Paper>
                </React.Fragment>
              );
            }

            if (this.fbOnboarding === true) {
              return (
                <Backdrop open={true} className={classes.backdrop}>
                  <div className={classes.backdropDiv}>
                    {integrationPage}
                  </div>
                </Backdrop>
              );  
            } else {
              return (
                <div className={classes.root}>
                  {integrationPage}
                </div>
              );
            }
          }
          return (
            <LoadingSpinner />
          );
        }}
      />
    );
  }
}

IntegrationSetupFreshbooks.propTypes = {
  classes: PropTypes.object.isRequired,
};
  
export default withStyles(styleSheet)(IntegrationSetupFreshbooks);
